/**
 * All Employees export (PDF, Excel, CSV). Builds the file from the rows the list is currently showing across ALL pages
 * (every employee that matches the active search and filters), never just the visible page. The columns are exactly the
 * visible table columns, so nothing sensitive (no SSN, bank, pay amount, date of birth or email) is ever written to a file.
 * Excel and PDF writers are loaded on demand so they do not weigh down the rest of the app.
 */
export type ExportFormat = 'csv' | 'xlsx' | 'pdf';

export const EXPORT_COLUMNS = ['Employee Name', 'Department', 'Employment Type', 'Pay Type', 'Pay Method', 'Status'] as const;

export type ExportRow = Record<(typeof EXPORT_COLUMNS)[number], string>;

export const EXPORT_FORMAT_LABELS: Record<ExportFormat, string> = { pdf: 'PDF', xlsx: 'Excel (.xlsx)', csv: 'CSV (.csv)' };

/** A spreadsheet treats a cell that starts with one of these as a formula, so a name such as "=HYPERLINK(...)" typed into
 * the app could run when the exported file is opened. A leading apostrophe makes it plain text. */
const FORMULA_LEAD = /^[=+\-@\t\r]/;

export function neutralizeFormula(value: string): string {
  return FORMULA_LEAD.test(value) ? `'${value}` : value;
}

export function csvEscape(value: string): string {
  const safe = neutralizeFormula(value);
  return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

export function toCsv(rows: readonly ExportRow[]): string {
  const lines = [EXPORT_COLUMNS.map(csvEscape).join(',')];
  for (const row of rows) lines.push(EXPORT_COLUMNS.map((c) => csvEscape(row[c])).join(','));
  // CRLF and a byte-order mark so Excel opens names with accents correctly.
  return `\uFEFF${lines.join('\r\n')}\r\n`;
}

export function exportFileName(format: ExportFormat, now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `employees-${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}.${format}`;
}

function download(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoke after the browser has started the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function exportEmployees(format: ExportFormat, rows: readonly ExportRow[], now: Date = new Date()): Promise<void> {
  const fileName = exportFileName(format, now);
  if (format === 'csv') {
    download(new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8' }), fileName);
    return;
  }
  if (format === 'xlsx') {
    const { default: writeXlsxFile } = await import('write-excel-file/browser');
    const header = EXPORT_COLUMNS.map((c) => ({ value: c, fontWeight: 'bold' as const }));
    const data = [header, ...rows.map((r) => EXPORT_COLUMNS.map((c) => ({ type: String, value: neutralizeFormula(r[c]) })))];
    const blob = await writeXlsxFile(data, { sheet: 'Employees' }).toBlob();
    download(blob, fileName);
    return;
  }
  const [{ jsPDF }, { default: autoTable }] = await Promise.all([import('jspdf'), import('jspdf-autotable')]);
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  doc.setFontSize(14);
  doc.text('All Employees', 40, 40);
  autoTable(doc, {
    startY: 56,
    head: [[...EXPORT_COLUMNS]],
    body: rows.map((r) => EXPORT_COLUMNS.map((c) => r[c])),
    styles: { fontSize: 9 },
    headStyles: { fillColor: [23, 92, 211] },
  });
  doc.save(fileName);
}
